// @flow
/** @jsx jsx */

import Downshift from 'downshift';
import { Component } from 'react';

import { TextInput } from '@zapier/design-system';
import { fuzzysearch } from '@zapier/common-utils';
import { css, jsx } from '@emotion/core';

import SuggestionsDropdown from './SuggestionsDropdown';
import { SuggestionsContext } from './SuggestionsContext';
import { hasOpenTag } from 'app/common/CommonUtils';

const wrapperCss = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: '100%',
});

type Props = {
  hasError: boolean,
  onBlur: () => void,
  onChangeValue: (value: string) => void,
  onFocus: () => void,
  placeholder: string,
  value: string,
};

class AutoCompleteTextInput extends Component<Props> {
  static contextType = SuggestionsContext;

  cursorPosition = this.props.value.length;

  get searchTerm() {
    const { value } = this.props;
    const startSearchPosition = value.lastIndexOf('{', this.cursorPosition) + 1;

    return value.slice(startSearchPosition, this.cursorPosition);
  }

  get filteredSuggestions() {
    return this.context.suggestions.filter(i =>
      fuzzysearch(this.searchTerm, i)
    );
  }

  itemToString = () => this.props.value;

  onSelect = (item: string) => {
    const { value, onChangeValue } = this.props;
    const tagStartPosition = value.lastIndexOf('{{', this.cursorPosition);
    const before = value.slice(0, tagStartPosition);
    const after = value.slice(this.cursorPosition);
    const autocompletedValue = `${before}{{${item}}}${after}`;

    onChangeValue(autocompletedValue);
  };

  onInputChange = (event: SyntheticEvent<HTMLInputElement>) => {
    const { selectionStart, value } = event.currentTarget;

    this.cursorPosition = selectionStart;
    this.props.onChangeValue(value);
  };

  onKeyDown = (event: SyntheticKeyboardEvent<HTMLInputElement>) => {
    const { props } = this;

    if (event.key === 'Tab' && hasOpenTag(props.value)) {
      event.preventDefault();
      const [item] = this.filteredSuggestions;
      if (item) {
        this.onSelect(item);
      }
    }
  };

  render() {
    const { props } = this;

    return (
      <Downshift
        isOpen={hasOpenTag(props.value)}
        itemToString={this.itemToString}
        onSelect={this.onSelect}
      >
        {({
          isOpen,
          highlightedIndex,
          getMenuProps,
          getItemProps,
          getInputProps,
          getRootProps,
        }) => {
          return (
            <div
              {...getRootProps({}, { suppressRefError: true })}
              css={wrapperCss}
            >
              <TextInput
                {...getInputProps({
                  onKeyDown: this.onKeyDown,
                })}
                // eslint-disable-next-line jsx-a11y/no-autofocus -- intentional focus-on-mount for this autocomplete input
                autoFocus={true}
                isErrored={props.hasError}
                onBlur={props.onBlur}
                onChange={this.onInputChange}
                onClick={props.onFocus}
                placeholder={props.placeholder}
                size="large"
                value={props.value}
              />
              {isOpen && (
                <SuggestionsDropdown
                  getItemProps={getItemProps}
                  getMenuProps={getMenuProps}
                  highlightedIndex={highlightedIndex}
                  items={this.filteredSuggestions}
                />
              )}
            </div>
          );
        }}
      </Downshift>
    );
  }
}

export default AutoCompleteTextInput;
